import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  http,
  getSubscriptionTrialvsPaidURL,
  getFlagsPerDayURL,
  getSongsPerDayURL,
  getAnalyticsEventsPerDayURL,
  getUsersPerDayURL,
  getSubscribersPerDayURL,
  getTopYoutubeImportsURL,
  getFailedYoutubeImportsURL,
  getUsersPerMonthURL,
  getSegmentEventsStudioURL,
  getSegmentEventsSongwriterURL,
} from '../services/http';

const DashboardContext = createContext();

export function useDashboardData() {
  return useContext(DashboardContext);
}

const ERROR_MSG = 'Could not read data from server. This is usually because of an ad-blocker, or because your session timed out. Try logging out and logging in again, and whitelisting this page.';

export function DashboardDataProvider({ children }) {
  const initial = { loading: true, data: [] };
  const [subscriptionsTrialvsPaid, setSubscriptionsTrialvsPaid] = useState({ loading: true, subscriptionNames: [], dataTrial: [], dataPaid: [] });
  const [flagsPerDay, setFlagsPerDay] = useState(initial);
  const [songsPerDay, setSongsPerDay] = useState(initial);
  const [analyticsEventsPerDay, setAnalyticsEventsPerDay] = useState(initial);
  const [usersPerDay, setUsersPerDay] = useState(initial);
  const [subscribersPerDay, setSubscribersPerDay] = useState(initial);
  const [topYoutubeImports, setTopYoutubeImports] = useState(initial);
  const [failedYoutubeImports, setFailedYoutubeImports] = useState(initial);
  const [usersPerMonth, setUsersPerMonth] = useState(initial);
  const [segmentEventsStudio, setSegmentEventsStudio] = useState(initial);
  const [segmentEventsSongwriter, setSegmentEventsSongwriter] = useState(initial);
  const [errors, setErrors] = useState({});

  const setError = (key) => setErrors(prev => ({ ...prev, [key]: ERROR_MSG }));

  useEffect(() => {
    // Subscriptions trial vs paid
    http.get(getSubscriptionTrialvsPaidURL()).then(({ data: subscriptions }) => {
      const subscriptionNames = subscriptions
        .map(s => s.subscription_name)
        .filter((v, i, a) => a.indexOf(v) === i);
      let dataTrial = [];
      let dataPaid = [];
      subscriptionNames.forEach(name => {
        const trialCount = subscriptions
          .filter(s => s.subscription_name === name && s.is_trial)
          .map(s => s.count);
        const paidCount = subscriptions
          .filter(s => s.subscription_name === name && !s.is_trial)
          .reduce((prev, cur) => prev + parseInt(cur.count), 0);
        dataTrial = [...dataTrial, ...trialCount];
        dataPaid = [...dataPaid, paidCount];
      });
      setSubscriptionsTrialvsPaid({ loading: false, subscriptionNames, dataTrial, dataPaid });
    }).catch(() => {
      setSubscriptionsTrialvsPaid({ loading: false, subscriptionNames: [], dataTrial: [], dataPaid: [] });
      setError('subscriptionsTrialvsPaid');
    });

    // Flags per day
    http.get(getFlagsPerDayURL()).then(({ data }) => {
      const dataAdjusted = data.map(flags => {
        if (flags.creation_date === '1666051200') {
          return { ...flags, subscription_paid_pro: '2', subscription_paid_plus: '4', subscription_paid_songwriter: '0' };
        }
        if (flags.creation_date >= '1670799600' && flags.creation_date <= '1673218800') {
          return { ...flags, login_my_scorecloud: '50' };
        }
        return flags;
      });
      setFlagsPerDay({ loading: false, data: dataAdjusted.map(item => ({ ...item, creation_date: item.creation_date * 1000 })) });
    }).catch(() => { setFlagsPerDay({ loading: false, data: [] }); setError('flagsPerDay'); });

    // Songs per day
    http.get(getSongsPerDayURL()).then(({ data }) => {
      setSongsPerDay({ loading: false, data: data.map(item => ({ ...item, creation_date: item.creation_date * 1000 })) });
    }).catch(() => { setSongsPerDay({ loading: false, data: [] }); setError('songsPerDay'); });

    // Analytics events per day
    http.get(getAnalyticsEventsPerDayURL()).then(({ data }) => {
      const dataAdjusted = data.map(items => {
        if (items.creation_date >= '1698278400' && items.creation_date <= '1698969600') {
          return { ...items, subscription_charged_successfully: '43', subscription_charged_successfully_sum: '477', subscription_charged_successfully_plus_sum: '151', subscription_charged_successfully_songwriter_sum: '76', subscription_charged_successfully_pro_sum: '251' };
        }
        if (items.creation_date >= '1702944000' && items.creation_date <= '1703635200') {
          return { ...items, video_import: '38' };
        }
        return items;
      });
      setAnalyticsEventsPerDay({ loading: false, data: dataAdjusted.map(item => ({ ...item, creation_date: item.creation_date * 1000 })) });
    }).catch(() => { setAnalyticsEventsPerDay({ loading: false, data: [] }); setError('analyticsEventsPerDay'); });

    // Users per day
    http.get(getUsersPerDayURL()).then(({ data }) => {
      setUsersPerDay({ loading: false, data: data.map(item => ({ ...item, creation_date: item.registration_date * 1000 })) });
    }).catch(() => { setUsersPerDay({ loading: false, data: [] }); setError('usersPerDay'); });

    // Subscribers per day
    http.get(getSubscribersPerDayURL()).then(({ data }) => {
      setSubscribersPerDay({ loading: false, data: data.map(item => ({ ...item, creation_date: item.date_created * 1000 })) });
    }).catch(() => { setSubscribersPerDay({ loading: false, data: [] }); setError('subscribersPerDay'); });

    // Top YouTube imports
    http.get(getTopYoutubeImportsURL()).then(({ data }) => {
      setTopYoutubeImports({ loading: false, data });
    }).catch(() => { setTopYoutubeImports({ loading: false, data: [] }); setError('topYoutubeImports'); });

    // Failed YouTube imports
    http.get(getFailedYoutubeImportsURL()).then(({ data }) => {
      setFailedYoutubeImports({ loading: false, data });
    }).catch(() => { setFailedYoutubeImports({ loading: false, data: [] }); setError('failedYoutubeImports'); });

    // Users per month
    http.get(getUsersPerMonthURL()).then(({ data }) => {
      setUsersPerMonth({ loading: false, data: data.map(item => ({ ...item, creation_date: item.creation_date * 1000 })) });
    }).catch(() => { setUsersPerMonth({ loading: false, data: [] }); setError('usersPerMonth'); });

    // Segment events studio
    http.get(getSegmentEventsStudioURL()).then(({ data }) => {
      setSegmentEventsStudio({ loading: false, data });
    }).catch(() => { setSegmentEventsStudio({ loading: false, data: [] }); setError('segmentEvents'); });

    // Segment events songwriter
    http.get(getSegmentEventsSongwriterURL()).then(({ data }) => {
      setSegmentEventsSongwriter({ loading: false, data });
    }).catch(() => { setSegmentEventsSongwriter({ loading: false, data: [] }); setError('segmentEvents'); });
  }, []);

  const analyticsData = { subscriptionsTrialvsPaid, flagsPerDay, songsPerDay, analyticsEventsPerDay, usersPerDay, subscribersPerDay, usersPerMonth };
  const youtubeData = { topYoutubeImports, failedYoutubeImports };
  const segmentEventData = { segmentEventsStudio, segmentEventsSongwriter };

  return (
    <DashboardContext.Provider value={{ analyticsData, youtubeData, segmentEventData, errors }}>
      {children}
    </DashboardContext.Provider>
  );
}
